import { Route, Routes, Navigate } from "react-router-dom";
import { routes } from "./utils/routes";
import { lazy, Suspense } from "react";
import { useSelector } from "react-redux";
import { RootState } from "./store/store";
import { Box, CircularProgress, Typography } from "@mui/material";
import Billing from "./components/Billing";
import Reselling from "./components/Reselling";
import ManagePlanPrice from "./components/ManagePlans/ManagePlanPrice";
import Agency from "./pages/Agency/Agency";
import GoogleBusinessImportSuccess from "./pages/Auth/AuthSuccess/GoogleBusinessImportSuccess";
import FacebookConnectSuccess from "./pages/Auth/AuthSuccess/FacebookConnectSuccess";
import Dashboard from "./components/Dashboard2/Dashboard";
import InstagramConnectSuccess from "./pages/Auth/AuthSuccess/InstagramConnectSuccess";
import InstagramFBConnectSuccess from "./pages/Auth/AuthSuccess/InstagramFBConnectSuccess";
import GoogleBusinessReconnectSuccess from "./pages/Auth/AuthSuccess/GoogleBusinessReconnectSuccess";
import ReportBuilderConnectReturn from "./pages/ReportBuilder/ReportBuilderConnectReturn";
import WorkspaceMembers from "./components/WorkspaceMembers/WorkspaceMembers";
import { getFirstAvailableRoute } from "./utils/moduleNavigation";
import AuthAppleSuccess from "./pages/Auth/AuthSuccess/AuthAppleSuccess";
import YoutubeConnectSuccess from "./pages/Auth/AuthSuccess/YoutubeConnectSuccess";
import TiktokConnectSuccess from "./pages/Auth/AuthSuccess/TiktokConnectSuccess";

const SignUp = lazy(() => import("./pages/Auth/SignUp"));
const Login = lazy(() => import("./pages/Auth/Login"));
const ForgotPassword = lazy(() => import("./pages/Auth/ForgotPassword"));
const LocalSEOHeatmapComponent = lazy(
  () => import("./components/LocalSEOHeatmap/LocalSEOHeatmap"),
);
const ManageLocalBusinessComponent = lazy(
  () => import("./components/ManageLocalBusiness/ManageLocalBusiness"),
);
const ResetPassword = lazy(() => import("./pages/Auth/ResetPassword"));
const MapComponent = lazy(() => import("./components/Map/Map"));
const AdminLogin = lazy(() => import("./pages/Admin/AdminLogin"));
const AdminDashboard2 = lazy(
  () => import("./components/Admin/AdminDashboard/AdminDashboard2"),
);
const AdminUsers2 = lazy(
  () => import("./components/Admin/AdminUsers/AdminUsers2"),
);
const AdminScans2 = lazy(
  () => import("./components/Admin/AdminScans/AdminScan2"),
);
const AdminBusiness2 = lazy(
  () => import("./components/Admin/AdminBusiness/AdminBusiness2"),
);
const Payment = lazy(() => import("./pages/Payment"));
const HistoryComponent = lazy(() => import("./components/History"));
const PublicReport = lazy(() => import("./pages/PublicReport"));
const PublicReviewRequest = lazy(
  () => import("./pages/PublicReviewRequest/index"),
);
const PublicReportBuilder = lazy(
  () => import("./pages/PublicReportBuilder/index"),
);
const AdminConfiguration2 = lazy(
  () => import("./components/Admin/AdminConfiguration/AdminConfiguration2"),
);
const AdminPlanSetting = lazy(
  () => import("./components/Admin/AdminPlanSetting"),
);
const CreditPayment = lazy(() => import("./pages/CreditPayment"));
const AdminManager = lazy(() => import("./components/Admin/AdminManager"));
const PaymentIncomplete = lazy(
  () => import("./pages/Payment/PaymentIncomplete"),
);
const CreditPaymentIncomplete = lazy(
  () => import("./pages/CreditPayment/CreditPaymentIncomplete"),
);
const RankingChecker = lazy(() => import("./pages/GuestUser/RankingChecker"));
const CTRBooster = lazy(() => import("./components/CTRBooster"));
const CTRBoosterForm = lazy(() => import("./components/CTRBoosterForm/Index"));
const ReportBuilder = lazy(() => import("./pages/ReportBuilder"));
const ScheduleACall = lazy(() => import("./components/ScheduleACall"));
const GetSetupDashboard = lazy(() => import("./components/GetSetupDashboard"));
const AdminCTRBooster2 = lazy(
  () => import("./components/Admin/AdminCTRBooster/AdminCTRBooster2"),
);
const AdminAddonPlan = lazy(() => import("./components/Admin/AdminAddonPlan"));
const MapSEO = lazy(() => import("./components/MapSEO"));
const ProtectedRoutes = lazy(
  () => import("./components/ProtectedRoutes/ProtectedRoutes"),
);
const Layout = lazy(() => import("./Layout/Layout"));
const Calender = lazy(
  () => import("./pages/MapCheckingProPages/Calender/Calender"),
);
const Reviews = lazy(
  () => import("./pages/MapCheckingProPages/Reviews/Reviews"),
);
const Setting = lazy(
  () => import("./pages/MapCheckingProPages/Setting/Setting"),
);
// const Widget = lazy(() => import('./pages/MapCheckingProPages/Widget/Widget'));
const WidgetV2 = lazy(
  () => import("./pages/MapCheckingProPages/WidgetV2/WidgetV2"),
);
const MDashboard = lazy(
  () => import("./pages/MapCheckingProPages/Dashboard/Dashboard"),
);
const TeamDetails = lazy(() => import("./components/TeamDetails/TeamDetails"));
const AddNewEmployee = lazy(
  () => import("./pages/MapCheckingProPages/AddNewEmployee/AddNewEmployee"),
);
const BusinessDashboardPage = lazy(
  () =>
    import("./pages/MapCheckingProPages/BusinessDashboard/BusinessDashboardPage"),
);
const VerifyEmployee = lazy(() => import("./components/VerifyEmployee"));
const AddMapCheckIn = lazy(
  () => import("./pages/MapCheckingProPages/Setting/AddMapCheckIn"),
);
const AdminGBPBusiness = lazy(
  () => import("./components/Admin/AdminGBPBusiness/AdminGBPBusiness"),
);
const AdminAudioSetting = lazy(
  () => import("./components/Admin/AdminAudioSetting/AdminAudioSetting"),
);
const AdminCrmImport = lazy(
  () => import("./components/Admin/AdminCrmImport/AdminCrmImport"),
);
const PublicRoute = lazy(
  () => import("./components/PublicRoutes.tsx/PublicRoutes"),
);
const DownloadPage = lazy(() => import("./pages/DownloadApp/DownloadPage"));
const OnboardingLayout = lazy(
  () => import("./components/Onboarding/OnboardingLayout"),
);
// const AdminTransferAccount = lazy(() => import('./components/Admin/AdminTransferAccount/AdminTransferAccount'));
const CompareBusinessLocation = lazy(
  () => import("./pages/CompareBusinessLocation"),
);
const AuthSuccess = lazy(() => import("./pages/Auth/AuthSuccess"));
const AcceptEject = lazy(() => import("./pages/Auth/AcceptEject"));
const AcceptInvite = lazy(() => import("./pages/Auth/AcceptInvite"));
const MapRankingPrivacyPolicy = lazy(
  () => import("./pages/MapRankingPrivacyPolicy"),
);
const MapRankingTermsAndConditions = lazy(
  () => import("./pages/MapRankingTermsAndConditions"),
);

const AppRoutes = () => {
  const { role, user, modules } = useSelector((state: RootState) => state.auth);

  const isLoggedIn = !!role && !!user;
  const isAuthReady = isLoggedIn ? !!modules : true;

  const AuthErrorRedirect = () => {
    const token = localStorage.getItem("pending_invite_token");
    const search = window.location.search;

    if (token) {
      localStorage.removeItem("pending_invite_token");
      const cleanSearch = search.startsWith("?") ? search.slice(1) : search;
      return (
        <Navigate
          to={`/accept-invite?token=${token}${cleanSearch ? `&${cleanSearch}` : ""}`}
          replace
        />
      );
    }

    return <Navigate to={{ pathname: routes.login, search }} replace />;
  };

  return (
    <Suspense
      fallback={
        <Box
          sx={{
            height: "100vh",
            width: "100vw",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#f4f6f8",
          }}
        >
          <CircularProgress size={60} thickness={5} />
          <Typography mt={2} variant="h6" sx={{ color: "#555" }}>
            Loading...
          </Typography>
        </Box>
      }
    >
      <Routes>
        <Route
          path="/"
          element={
            !isAuthReady ? (
              <Box
                sx={{
                  height: "100vh",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <Navigate
                to={{
                  pathname: isLoggedIn
                    ? getFirstAvailableRoute(modules)
                    : routes.login,
                  search: window.location.search,
                }}
                replace
              />
            )
          }
        />
        <Route path="/auth/error" element={<AuthErrorRedirect />} />
        <Route
          path="/*"
          element={
            !isAuthReady ? null : (
              <Navigate
                to={{
                  pathname: isLoggedIn
                    ? getFirstAvailableRoute(modules)
                    : routes.login,
                  search: window.location.search,
                }}
                replace
              />
            )
          }
        />
        <Route
          path="/admin"
          element={
            <Navigate
              to={isLoggedIn ? routes.adminDashboard : routes.adminLogin}
              replace
            />
          }
        />
        <Route path="/ranking-checker" element={<RankingChecker />} />

        <Route element={<PublicRoute />}>
          <Route path={routes?.signup} element={<SignUp />} />
          <Route path={routes?.login} element={<Login />} />
          <Route path={routes?.adminLogin} element={<AdminLogin />} />
        </Route>

        <Route path={routes?.download} element={<DownloadPage />} />
        <Route path={routes?.onboarding} element={<OnboardingLayout />} />
        <Route path={routes?.onboardingStep} element={<OnboardingLayout />} />
        <Route
          path={routes?.maprankingPrivacyPolicy}
          element={<MapRankingPrivacyPolicy />}
        />
        <Route
          path={routes?.maprankingTermsAndConditions}
          element={<MapRankingTermsAndConditions />}
        />
        <Route path={routes?.authSuccess} element={<AuthSuccess />} />
        <Route path={routes?.authAppleSuccess} element={<AuthAppleSuccess />} />
        <Route
          path={routes?.businessImportSuccess}
          element={<GoogleBusinessImportSuccess isgbp={false} />}
        />
        <Route
          path={routes?.businessFetchSuccess}
          element={<GoogleBusinessImportSuccess isgbp={true} />}
        />
        <Route
          path={routes?.facebookSuccess}
          element={<FacebookConnectSuccess />}
        />
        <Route
          path={routes?.googleBusinessReconnectSuccess}
          element={<GoogleBusinessReconnectSuccess />}
        />
        <Route
          path={routes?.reportBuilderConnect}
          element={<ReportBuilderConnectReturn />}
        />
        <Route
          path={routes?.instagramSuccess}
          element={<InstagramConnectSuccess />}
        />
        <Route
          path={routes?.instagramFBSuccess}
          element={<InstagramFBConnectSuccess />}
        />
        <Route
          path={routes?.youtubeSuccess}
          element={<YoutubeConnectSuccess />}
        />
        <Route
          path={routes?.tiktokSuccess}
          element={<TiktokConnectSuccess />}
        />
        <Route path={routes?.forgotpassword} element={<ForgotPassword />} />
        <Route path={routes?.resetpassword} element={<ResetPassword />} />
        <Route
          path={routes?.resetEmployeepassword}
          element={<ResetPassword />}
        />
        <Route path={routes?.payment} element={<Payment />} />
        <Route
          path={routes?.paymentIncomplete}
          element={<PaymentIncomplete />}
        />
        <Route path={routes?.publicReport} element={<PublicReport />} />
        <Route
          path={routes?.publicReportBuilder}
          element={<PublicReportBuilder />}
        />
        <Route
          path={routes?.publicReviewRequest}
          element={<PublicReviewRequest />}
        />
        <Route path={routes?.guestCheckerReport} element={<MapComponent />} />
        <Route path={routes?.creditPayment} element={<CreditPayment />} />
        <Route
          path={routes?.creditPaymentIncomplete}
          element={<CreditPaymentIncomplete />}
        />
        <Route path={routes.verfiyEmployee} element={<VerifyEmployee />} />
        <Route path={routes.acceptTransfer} element={<AcceptEject />} />
        <Route path={routes.acceptInvite} element={<AcceptInvite />} />

        <Route
          element={
            <ProtectedRoutes allowedRoles={["user"]}>
              <Layout />
            </ProtectedRoutes>
          }
        >
          {modules?.rank_tracker !== false && (
            <>
              <Route
                path={routes.localSEOHeatmap}
                element={<LocalSEOHeatmapComponent />}
              />
              <Route
                path={routes.manageLocalBusiness}
                element={<ManageLocalBusinessComponent />}
              />
            </>
          )}

          {modules?.map_checkins !== false && (
            <>
              {/* <Route path={routes?.dashboard} element={<Dashboard />} /> */}
              <Route path={routes?.dashboard} element={<GetSetupDashboard />} />
            </>
          )}
          <Route path={routes?.map} element={<MapComponent />} />

          {modules?.billing !== false && (
            <>
              <Route path={routes?.upgrade} element={<ManagePlanPrice />} />
              <Route path={routes?.billing} element={<Billing />} />
            </>
          )}
          {modules?.reselling !== false && (
            <Route path={routes?.reselling} element={<Reselling />} />
          )}

          <Route path={routes?.history} element={<HistoryComponent />} />

          {modules?.ctr !== false && (
            <>
              <Route path={routes?.ctrBooster} element={<CTRBooster />} />
              <Route
                path={routes?.ctrBoosterForm}
                element={<CTRBoosterForm />}
              />
            </>
          )}

          <Route path={routes?.reportBuilder} element={<ReportBuilder />} />

          {modules?.book_call !== false && (
            <Route path={routes?.scheduleACall} element={<ScheduleACall />} />
          )}
          <Route path={routes?.mapSEO} element={<MapSEO />} />

          {modules?.map_checkins !== false && (
            <>
              <Route path={routes.mapCheckingCalender} element={<Calender />} />
              <Route path={routes.mapCheckingReviews} element={<Reviews />} />
              <Route
                path={routes.mapCheckingDashboard}
                element={<MDashboard />}
              />
              <Route path={routes.mapCheckingSetting} element={<Setting />} />
              {/* <Route path={routes.mapCheckingWidget} element={<Widget />} /> */}
              <Route path={routes.mapCheckingWidget} element={<WidgetV2 />} />
              <Route path={routes.mapchekcingteam} element={<TeamDetails />} />
              <Route path={routes.addEmployee} element={<AddNewEmployee />} />
              <Route
                path={routes.businessdashboard}
                element={<BusinessDashboardPage />}
              />
              <Route path={routes.addMapCheckIn} element={<AddMapCheckIn />} />
            </>
          )}

          <Route
            path={routes.compareBusinessLocation}
            element={<CompareBusinessLocation />}
          />

          {modules?.white_label !== false && (
            <Route path={routes?.whitelabel} element={<Agency />} />
          )}

          {modules?.team !== false && (
            <Route
              path={routes?.workspaceMember}
              element={<WorkspaceMembers />}
            />
          )}
        </Route>

        <Route
          element={
            <ProtectedRoutes allowedRoles={["admin"]}>
              <Layout />
            </ProtectedRoutes>
          }
        >
          <Route path={routes.adminManager} element={<AdminManager />} />
          <Route
            path={routes?.adminConfiguration}
            element={<AdminConfiguration2 />}
          />
          <Route
            path={routes?.adminPlanSetting}
            element={<AdminPlanSetting />}
          />
          <Route path={routes?.adminAddonPlan} element={<AdminAddonPlan />} />
        </Route>

        <Route
          element={
            <ProtectedRoutes allowedRoles={["admin", "manager"]}>
              <Layout />
            </ProtectedRoutes>
          }
        >
          <Route
            path={routes?.admin}
            element={<Navigate replace to={routes?.adminLogin} />}
          />
          <Route path={routes?.adminDashboard} element={<AdminDashboard2 />} />
          <Route path={routes?.adminUsers} element={<AdminUsers2 />} />
          <Route path={routes?.adminBusiness} element={<AdminBusiness2 />} />
          <Route path={routes?.adminScans} element={<AdminScans2 />} />
          <Route
            path={routes?.adminCTRBooster}
            element={<AdminCTRBooster2 />}
          />
          <Route
            path={routes?.adminConfiguration}
            element={<AdminConfiguration2 />}
          />
          <Route
            path={routes?.adminPlanSetting}
            element={<AdminPlanSetting />}
          />
          <Route path={routes?.adminAddonPlan} element={<AdminAddonPlan />} />
          <Route
            path={routes.localSEOHeatmapManager}
            element={<LocalSEOHeatmapComponent />}
          />
          <Route path={routes.managerMap} element={<MapComponent />} />
          <Route path={routes.adminManager} element={<AdminManager />} />
          <Route
            path={routes.adminGBPBusiness}
            element={<AdminGBPBusiness />}
          />
          <Route
            path={routes.adminAudioSetting}
            element={<AdminAudioSetting />}
          />
          <Route path={routes.adminCrmImport} element={<AdminCrmImport />} />
          {/* <Route path={routes.adminTransferAccount} element={<AdminTransferAccount />} /> */}
          <Route
            path={routes.adminCompareBusinessLocation}
            element={<CompareBusinessLocation />}
          />
        </Route>
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
